'use client'

import {
    MainNavigationListItemContext,
    MainNavigationListItemProvider,
} from './MainNavigationItem/MainNavigationListItemContext'
import { useOnClickOutside } from '../../../hooks/useOutsideClick'
import React, { KeyboardEvent, useCallback, useContext, useRef } from 'react'

interface Props {
    children: React.ReactNode
}

/**
 * Internal list item that wires pointer and keyboard interactions for a single main navigation entry.
 *
 * - wraps content in an `<li>` for semantic list structure
 * - manages open/close behaviour through `MainNavigationItemContext`
 * - closes the submenu on outside click, mouse leave, or Escape key
 */
const MainNavigationListItemInternal = ({ children }: Props) => {
    const context = useContext(MainNavigationListItemContext)
    const containerRef = useRef<HTMLLIElement | null>(null)

    const focusWithinClasses =
        'focus-within:outline-2 focus-within:outline-accent-600 focus-within:rounded-xs'

    const handleOutsideClick = useCallback(() => {
        if (context?.isOpen) {
            context?.close()
        }
    }, [context?.close, context?.isOpen])

    useOnClickOutside(containerRef, handleOutsideClick)

    if (!context) {
        return <li className={`main-navigation__item-wrapper relative h-full ${focusWithinClasses}`}>{children}</li>
    }
    const { close, toggle, overlay } = context
    const isOpen = overlay.triggerAriaProps['aria-expanded']

    const handleMouseEnter = () => {
        if (!isOpen) {
            toggle()
        }
    }

    const handleMouseLeave = () => {
        if (isOpen) {
            close()
        }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
        if (event.key === 'Escape') {
            if (isOpen) {
                event.stopPropagation()
                close()
            }

            overlay.focusTrigger()
        }
    }

    return (
        // Disable the linting rule because we need to add a keyboard event listener to the list item. Buttons (this component's child) do not get onKeyDown events.
        // As for the hover events, these are only for mouse users.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <li
            ref={containerRef}
            className={`a11y-outline main-navigation__item-wrapper relative h-full ${focusWithinClasses} ${
                isOpen ? 'main-navigation__item-wrapper--show-subitems' : 'main-navigation__item-wrapper--hide-subitems'
            }`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onKeyDown={handleKeyDown}
        >
            {children}
        </li>
    )
}

export const MainNavigationListItem = ({ children }: Props) => {
    return (
        <MainNavigationListItemProvider>
            <MainNavigationListItemInternal>{children}</MainNavigationListItemInternal>
        </MainNavigationListItemProvider>
    )
}
